interface MermaidRenderResult {
  svg: string;
  bindFunctions?: (el: Element) => void;
}

interface MermaidLike {
  initialize: (config: Record<string, unknown>) => void;
  render: (id: string, code: string) => Promise<MermaidRenderResult>;
}

const MERMAID_CDN = 'https://cdn.jsdelivr.net/npm/mermaid@11/dist/mermaid.esm.min.mjs';

let mermaidPromise: Promise<MermaidLike> | null = null;
let idCounter = 0;

export function loadMermaid(): Promise<MermaidLike> {
  if (!mermaidPromise) {
    mermaidPromise = import(/* @vite-ignore */ MERMAID_CDN).then((mod) => {
      const mermaid: MermaidLike = mod.default ?? mod;
      mermaid.initialize({ startOnLoad: false, securityLevel: 'loose' });
      return mermaid;
    });
  }
  return mermaidPromise;
}

function dedent(text: string): string {
  const lines = text.replace(/^\n+|\s+$/g, '').split('\n');
  const indents = lines
    .filter((l) => l.trim().length)
    .map((l) => (l.match(/^[ \t]*/) ?? [''])[0].length);
  const min = indents.length ? Math.min(...indents) : 0;
  return lines.map((l) => l.slice(min)).join('\n');
}

export type RenderTarget = string | HTMLElement;

export async function render(
  target: RenderTarget,
  markdown: string,
  customCss?: string,
): Promise<void> {
  const host =
    typeof target === 'string'
      ? document.querySelector<HTMLElement>(target)
      : target;
  if (!host) throw new Error('mermaid-embed: target element not found');

  const root = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
  root.innerHTML = '';

  const style = document.createElement('style');
  style.textContent = `
    :host { display: block; }
    .mermaid-embed-root { width: 100%; overflow: auto; }
    .mermaid-embed-root svg { max-width: 100%; height: auto; }
    .mermaid-embed-error {
      color: #b00020;
      font: 13px/1.4 ui-monospace, SFMono-Regular, Menlo, monospace;
      white-space: pre-wrap;
      padding: 8px 10px;
      border: 1px solid #f2c2cb;
      background: #fff5f6;
      border-radius: 4px;
    }
    ${customCss ?? ''}
  `;
  root.appendChild(style);

  const container = document.createElement('div');
  container.className = 'mermaid-embed-root';
  root.appendChild(container);

  const code = dedent(markdown ?? '');
  if (!code) return;

  try {
    const mermaid = await loadMermaid();
    const id = `mermaid-embed-${++idCounter}-${Date.now().toString(36)}`;
    const { svg, bindFunctions } = await mermaid.render(id, code);
    container.innerHTML = svg;
    if (bindFunctions) bindFunctions(container);
  } catch (err) {
    const pre = document.createElement('pre');
    pre.className = 'mermaid-embed-error';
    const message = err instanceof Error ? err.message : String(err);
    pre.textContent = `Mermaid render error:\n${message}`;
    container.appendChild(pre);
  }
}

function getMarkdown(el: HTMLElement): string {
  const tpl = el.querySelector(':scope > template');
  if (tpl) return (tpl as HTMLTemplateElement).innerHTML;
  const script = el.querySelector(':scope > script[type="text/mermaid"]');
  if (script) return script.textContent ?? '';
  return el.getAttribute('data-mermaid') ?? el.textContent ?? '';
}

function getCss(el: HTMLElement): string {
  const cssAttr = el.getAttribute('data-css');
  if (cssAttr) return cssAttr;
  const cssEl = el.querySelector(':scope > style[data-embed-css]');
  return cssEl?.textContent ?? '';
}

export function autoInit(root: ParentNode = document): void {
  const nodes = root.querySelectorAll<HTMLElement>(
    '.mermaid-embed:not([data-mermaid-embed-ready])',
  );
  nodes.forEach((el) => {
    el.setAttribute('data-mermaid-embed-ready', '');
    const markdown = getMarkdown(el);
    const css = getCss(el);
    el.textContent = '';
    void render(el, markdown, css);
  });
}

const MermaidEmbed = { render, autoInit, loadMermaid };
export type MermaidEmbedApi = typeof MermaidEmbed;

declare global {
  interface Window {
    MermaidEmbed: MermaidEmbedApi;
  }
}

if (typeof window !== 'undefined') {
  window.MermaidEmbed = MermaidEmbed;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => autoInit());
  } else {
    autoInit();
  }
}

export { MermaidEmbed };
